import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Emblem } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { IMG } from "@/constants/images";

export default function Auth({ mode = "login" }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", token: params.get("token") || "" });
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [agree, setAgree] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !agree) {
      toast.error("Please confirm you are 18+ and agree to the Terms, Privacy & Data policies.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password, remember);
        toast.success("Welcome back, Chef.");
        nav("/dashboard");
      } else if (mode === "signup") {
        const u = await register(form.name, form.email, form.password);
        toast.success("Account created.");
        nav(u.profile?.completed ? "/dashboard" : "/onboarding");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If that email exists, a reset link was sent. (Check server logs in demo.)");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: form.token, password: form.password });
        toast.success("Password updated. Please log in.");
        nav("/login");
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: ["Welcome back", "Sign in to your command center."],
    signup: ["Create your account", "Start turning ideas into plated menus."],
    forgot: ["Reset password", "Enter your email to receive a reset link."],
    reset: ["Set a new password", "Choose a new password for your account."],
  };
  const [title, subtitle] = titles[mode];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ink">
      <div className="hidden lg:block relative grain">
        <img src={IMG.darkBowl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-ink via-ink/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2.5"><Emblem className="h-9 w-9" />
            <span className="font-serif text-xl gold-text tracking-wider">CHEF PLATED</span></Link>
          <div>
            <h2 className="font-serif text-5xl leading-tight">From inspiration<br /><span className="gold-text">to execution.</span></h2>
            <p className="text-white/50 mt-4 max-w-sm">Imagine it. Plate it. Cost it. Order it.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-8 justify-center">
            <Emblem className="h-9 w-9" /><span className="font-serif text-xl gold-text tracking-wider">CHEF PLATED</span></Link>
          <h1 className="font-serif text-4xl">{title}</h1>
          <p className="text-white/50 mt-2 mb-8">{subtitle}</p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <Field label="Full name" value={form.name} onChange={set("name")} testid="auth-name" required />
            )}
            {(mode === "login" || mode === "signup" || mode === "forgot") && (
              <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="auth-email" required />
            )}
            {(mode === "login" || mode === "signup" || mode === "reset") && (
              <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="auth-password" required />
            )}
            {mode === "login" && (
              <label className="flex items-center gap-2.5 text-sm text-white/70 cursor-pointer select-none" data-testid="remember-me-label">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-me-checkbox" className="accent-[#D4AF37] h-4 w-4" />
                Keep me signed in on this device
              </label>
            )}
            {mode === "signup" && (
              <label className="flex items-start gap-2.5 text-sm text-white/70 cursor-pointer select-none" data-testid="agree-terms-label">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="agree-terms-checkbox" className="accent-[#D4AF37] h-4 w-4 mt-0.5 shrink-0" />
                <span>I confirm I am 18 or older and agree to the <Link to="/terms" target="_blank" className="text-gold hover:underline">Terms of Service</Link>, <Link to="/privacy" target="_blank" className="text-gold hover:underline">Privacy Policy</Link> &amp; <Link to="/data" target="_blank" className="text-gold hover:underline">Data Policy</Link>.</span>
              </label>
            )}
            <button type="submit" disabled={loading || (mode === "signup" && !agree)} data-testid="auth-submit"
              className="btn-gold w-full py-4 text-sm uppercase tracking-widest rounded-sm disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : mode === "signup" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Update Password"}
            </button>
          </form>
          <div className="mt-6 text-sm text-white/50 space-y-2">
            {mode === "login" && <>
              <p><Link to="/forgot-password" className="text-gold hover:underline">Forgot password?</Link></p>
              <p>No account? <Link to="/signup" className="text-gold hover:underline">Sign up</Link></p>
            </>}
            {mode === "signup" && <p>Already have an account? <Link to="/login" className="text-gold hover:underline">Sign in</Link></p>}
            {(mode === "forgot" || mode === "reset") && <p><Link to="/login" className="text-gold hover:underline">Back to login</Link></p>}
          </div>
          <p className="mt-6 text-xs text-white/35">
            By continuing you agree to our <Link to="/terms" className="text-white/55 hover:text-gold" data-testid="auth-terms-link">Terms</Link>, <Link to="/privacy" className="text-white/55 hover:text-gold" data-testid="auth-privacy-link">Privacy Policy</Link> &amp; <Link to="/data" className="text-white/55 hover:text-gold" data-testid="auth-data-link">Data Policy</Link>.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid}
        className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none
        focus:border-gold/60 transition-colors" />
    </div>
  );
}
